import type { CompetitorRecord } from "@/lib/compare/types";

const HELP_TIERS = "https://www.capcut.com/help/difference-between-standard-and-pro-subscription";
const CREDITS_RULE = "https://www.capcut.com/clause/credits-rule";
const SOCIALRAILS = "https://socialrails.com/blog/capcut-pricing-guide";
const LONG_TO_SHORT = "https://www.capcut.com/resource/turn-long-video-to-short-video-with-ai";
const CHECKED = "2026-10-08";
const THIRD_PARTY = "third-party figure; CapCut publishes no pricing page";

export const CAPCUT: CompetitorRecord = {
  slug: "capcut",
  name: "CapCut",
  aliases: ["Cap Cut", "CapCut Pro", "capcut.com"],
  url: "https://www.capcut.com",
  pricingUrl: HELP_TIERS,
  category: "editor",
  positioning: "Free-first all-in-one video editor for mobile, desktop, web and pad from ByteDance.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "Credits consumed by Motion effects and AI effects; allowance not published",
      includes: [
        "1080p export cap",
        "Watermark on templates and effects, plus an end card",
        "Free 1 GB cloud storage removed Aug 2024",
        "Basic editing on every platform",
      ],
      sourceUrl: SOCIALRAILS,
      checkedAt: CHECKED,
      note: "Free plan limits are third-party figures; CapCut publishes no pricing page",
    },
    {
      name: "Standard",
      monthly: 9.99,
      metered: "Subscription credits valid 1 month, consumed by Motion and AI effects",
      includes: [
        "Monthly billing only",
        "Mobile-first tier",
        "100 GB cloud storage",
        "Basic AI features",
        "Auto captions not listed; they are a Pro feature on CapCut's help page",
      ],
      sourceUrl: SOCIALRAILS,
      checkedAt: CHECKED,
      note: THIRD_PARTY,
    },
    {
      name: "Pro",
      monthly: 19.99,
      annualPerMonth: 15,
      annualTotal: 179.99,
      metered: "Subscription credits valid 1 month; AI credit allowance disputed across sources",
      includes: [
        "Mobile, desktop and web",
        "1 TB cloud storage",
        "Camera tracking",
        "Auto captions",
        "AI avatars, AI images and AI video",
      ],
      sourceUrl: SOCIALRAILS,
      checkedAt: CHECKED,
      note: THIRD_PARTY,
    },
    {
      name: "Teams",
      monthly: 24.99,
      annualPerMonth: 17.92,
      annualTotal: 214.99,
      metered: "Per user; credit allowance not published",
      includes: ["Priced per user", "Up to 4 members", "Everything in Pro"],
      sourceUrl: SOCIALRAILS,
      checkedAt: CHECKED,
      note: THIRD_PARTY,
    },
  ],
  entryPlanName: "Standard",
  entryPlanIncludes:
    "Mobile-first tier with 100 GB storage and basic AI, monthly only; auto captions need Pro (third-party figure)",
  freeTier: "Free editor with a 1080p cap, a watermark on templates and effects, and an end card",
  trial: "Not stated on CapCut's help page or in the third-party guides checked",
  meteringUnit:
    "Three credit types: subscription credits valid 1 month, purchased credits valid 2 years, activity credits; spent on Motion and AI effects",
  exportResolution: "1080p cap on Free per third-party guides; the Pro ceiling is not stated on the help page",
  features: {
    longToShort: { v: "yes", note: "Long video to shorts tool in CapCut Online", source: LONG_TO_SHORT },
    momentRanking: { v: "no", note: "No virality ranking documented" },
    reframing: { v: "partial", note: "Camera Tracking is Pro; no auto reframe for clips documented" },
    maxSource: { v: "n/a", note: "Not published" },
    layouts: { v: "n/a", note: "Not enumerated for the clip tool" },
    redditStory: { v: "no", note: "Community templates, no generator" },
    fakeText: { v: "no", note: "Community templates, no generator" },
    aiImages: { v: "yes", note: "Pro feature per the help page" },
    voiceover: { v: "yes", note: "Text to speech" },
    voiceCloning: { v: "n/a", note: "Not listed on the help page" },
    avatars: { v: "yes", note: "AI avatars on Pro" },
    timelineEditor: { v: "yes", note: "Full editor on Free, many effects Pro-badged" },
    captionStyles: { v: "n/a", note: "Count not published" },
    captionLanguages: { v: "text", note: "Up to 20, auto captions on Pro" },
    bRoll: { v: "no", note: "No auto B-roll" },
    fourK: { v: "n/a", note: "Free capped at 1080p; Pro ceiling not stated" },
    scheduling: { v: "no", note: "Share to TikTok only" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "No public API" },
    webhooks: { v: "no" },
    web: { v: "yes", note: "CapCut Online" },
    desktop: { v: "yes", note: "Mac and Windows, works offline" },
    mobile: { v: "yes", note: "iOS and Android" },
    teamSeats: { v: "yes", note: "Teams plan, up to 4 members" },
    freeTier: { v: "yes", note: "Watermark on templates and effects, end card" },
    trial: { v: "n/a", note: "Not stated" },
    metering: { v: "text", note: "Credits spent on Motion and AI effects" },
    rollover: { v: "no", note: "Subscription credits expire after 1 month" },
    refunds: { v: "text", note: "Credits are non-refundable per the credits rule" },
    cancelInApp: { v: "n/a", note: "Not described on the help page" },
  },
  billingTerms: {
    refunds:
      "CapCut's credits rule page states credits are non-refundable. No refund terms for subscriptions were found on a CapCut page as of Oct 8, 2026.",
    cancel: "Cancellation steps are not described on CapCut's help or pricing pages; the pricing page returns a 404.",
    sourceUrl: CREDITS_RULE,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "Seems to be impossible to cancel a Pro subscription",
      date: "2026-10-01",
      url: "https://www.trustpilot.com/review/capcut.com",
      evidences: "cancelInApp",
    },
    {
      text: "it's asking for more money for pro features",
      date: "2026-10-08",
      url: "https://www.trustpilot.com/review/capcut.com",
      evidences: "metering",
    },
  ],
  verification: {
    method: "third-party",
    date: CHECKED,
    note: "capcut.com/pricing returned a 404 on Oct 8, 2026 and the homepage shows no Pro price, so every dollar figure here is third-party: Standard about $9.99 monthly-only, Pro $19.99/mo or $179.99/yr, Teams $24.99/user/mo or $214.99/user/yr, Free 1080p cap with watermark. Sources: socialrails.com CapCut pricing guide (updated Jul 18, 2026), eesel.ai CapCut pricing article (May 20, 2026), and VEED's veed-vs-capcut article; the three agree on these figures. Pro's AI credit allowance conflicts across them (1,200, 550 or about 200) and is left unstated. App-store prices run $1 to $3 higher. All four prices need a human browser check inside the CapCut app before publication; tier contents come from CapCut's own help page and credit rules from its credits-rule page.",
  },
  seoPattern: "capcut.com/resource/capcut-vs-<x> and /resource/best-alternatives-to-<x>, no page about Crayo",
};
